import { useEffect } from 'react';
import { translateAdminPhrase, type Language } from '../lib/i18n';
import { getCanonicalEnglishPhrase } from '../lib/adminTranslations';

const textOriginals = new WeakMap<Text, string>();
const attrOriginals = new WeakMap<Element, Map<string, string>>();
const translatedAttributes = ['placeholder', 'aria-label', 'title'] as const;

// Determines if a text node's parent should be skipped (e.g. form inputs, code, raw svg, or user data)
const shouldSkipTextNode = (element: Element | null): boolean => {
  if (!element) return true;
  return Boolean(
    element.closest(
      'script, style, svg, code, pre, textarea, input, select, option, button[data-no-translate], [contenteditable="true"], [data-no-admin-i18n], [data-no-translate], [data-user-content], .no-translate'
    )
  );
};

// Determines if an element's attributes (placeholder, title, aria-label) should be skipped
const shouldSkipAttributes = (element: Element | null): boolean => {
  if (!element) return true;
  return Boolean(
    element.closest(
      'script, style, svg, code, pre, button[data-no-translate], [data-no-admin-i18n], [data-no-translate], [data-user-content], .no-translate'
    )
  );
};

const isNumericOrSymbolOnly = (text: string): boolean => {
  const trimmed = text.trim();
  return (
    !trimmed ||
    trimmed.length === 1 ||
    /^[\d\s.,:/$€£¥\-–—%+*#@()]+$/.test(trimmed) ||
    /^(https?:\/\/|[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,})/.test(trimmed)
  );
};

const translateTextNode = (node: Text, language: Language) => {
  const parent = node.parentElement;
  if (shouldSkipTextNode(parent)) return;

  const currentVal = node.nodeValue || '';
  if (isNumericOrSymbolOnly(currentVal)) return;

  // Canonicalize to English key even if node is already translated
  const canonical = getCanonicalEnglishPhrase(currentVal);
  if (!textOriginals.has(node) || canonical !== currentVal) {
    textOriginals.set(node, canonical);
  }

  const original = textOriginals.get(node) || canonical;
  const translated = translateAdminPhrase(language, original);
  if (node.nodeValue !== translated) {
    node.nodeValue = translated;
  }
};

const translateElementAttributes = (element: Element, language: Language) => {
  if (shouldSkipAttributes(element)) return;

  translatedAttributes.forEach(attribute => {
    const currentValue = element.getAttribute(attribute);
    if (!currentValue || isNumericOrSymbolOnly(currentValue)) return;

    const originalMap = attrOriginals.get(element) || new Map<string, string>();
    if (!attrOriginals.has(element)) attrOriginals.set(element, originalMap);
    
    const canonical = getCanonicalEnglishPhrase(currentValue);
    if (!originalMap.has(attribute) || canonical !== currentValue) {
      originalMap.set(attribute, canonical);
    }

    const original = originalMap.get(attribute) || canonical;
    const translated = translateAdminPhrase(language, original);
    if (element.getAttribute(attribute) !== translated) {
      element.setAttribute(attribute, translated);
    }
  });
};

const translateTree = (root: ParentNode, language: Language) => {
  if (root instanceof Element) {
    translateElementAttributes(root, language);
  }

  const elementWalker = document.createTreeWalker(root, NodeFilter.SHOW_ELEMENT);
  while (elementWalker.nextNode()) {
    translateElementAttributes(elementWalker.currentNode as Element, language);
  }

  const textWalker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT, {
    acceptNode: node => {
      const value = node.nodeValue || '';
      if (!value.trim()) return NodeFilter.FILTER_REJECT;
      return shouldSkipTextNode(node.parentElement) ? NodeFilter.FILTER_REJECT : NodeFilter.FILTER_ACCEPT;
    },
  });

  while (textWalker.nextNode()) {
    translateTextNode(textWalker.currentNode as Text, language);
  }
};

export const useAdminDomTranslations = (language: Language) => {
  useEffect(() => {
    const root = document.querySelector('.admin-shell');
    if (!root) return;

    translateTree(root, language);

    const observer = new MutationObserver(mutations => {
      mutations.forEach(mutation => {
        mutation.addedNodes.forEach(node => {
          if (node.nodeType === Node.TEXT_NODE) {
            translateTextNode(node as Text, language);
          }
          if (node.nodeType === Node.ELEMENT_NODE) {
            translateTree(node as Element, language);
          }
        });
      });
    });

    observer.observe(root, { childList: true, subtree: true });
    return () => observer.disconnect();
  }, [language]);
};
